/**
 * User identity management with persistent anonymous IDs
 */

const ANONYMOUS_ID_KEY = 'wt_anonymous_id';
const FIRST_SEEN_KEY = 'wt_first_seen';
const LAST_SEEN_KEY = 'wt_last_seen';
const VISIT_COUNT_KEY = 'wt_visit_count';

/**
 * Generate a unique anonymous user ID
 */
export function generateAnonymousId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 15);
  const random2 = Math.random().toString(36).substring(2, 15);
  return `anon_${timestamp}_${random}${random2}`;
}

/**
 * Get or create anonymous user ID
 * This persists across sessions and is the primary way to track users before they identify
 */
export function getAnonymousId(): string {
  try {
    let anonymousId = localStorage.getItem(ANONYMOUS_ID_KEY);

    if (!anonymousId) {
      anonymousId = generateAnonymousId();
      localStorage.setItem(ANONYMOUS_ID_KEY, anonymousId);
      localStorage.setItem(FIRST_SEEN_KEY, new Date().toISOString());
      localStorage.setItem(VISIT_COUNT_KEY, '1');
    } else {
      // Increment visit count
      const count = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '1', 10);
      localStorage.setItem(VISIT_COUNT_KEY, (count + 1).toString());
    }

    localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());

    return anonymousId;
  } catch {
    // Fallback if localStorage is not available
    return generateAnonymousId();
  }
}


/**
 * Get user visit statistics
 */
export function getUserVisitStats() {
  try {
    return {
      firstSeen: localStorage.getItem(FIRST_SEEN_KEY) || null,
      lastSeen: localStorage.getItem(LAST_SEEN_KEY) || null,
      visitCount: parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '1', 10),
    };
  } catch {
    return {
      firstSeen: null,
      lastSeen: null,
      visitCount: 1,
    };
  }
}

/**
 * Clear anonymous identity (useful for logout)
 */
export function clearAnonymousIdentity(): void {
  try {
    localStorage.removeItem(ANONYMOUS_ID_KEY);
    // Note: We keep device_id, first_seen, and visit stats for analytics continuity
  } catch {
    // Silent fail
  }
}

/**
 * Link anonymous ID to authenticated user
 * Returns the anonymous ID that should be sent to backend for user merging
 */
export function linkIdentity(userId: string): string | null {
  try {
    const anonymousId = localStorage.getItem(ANONYMOUS_ID_KEY);
    // Store mapping for future reference
    if (anonymousId) {
      localStorage.setItem(`wt_linked_${userId}`, anonymousId);
    }
    return anonymousId;
  } catch {
    return null;
  }
}

/**
 * Get comprehensive identity information
 */
export function getIdentityInfo() {
  const stats = getUserVisitStats();
  const anonymousId = getAnonymousId();

  return {
    anonymousId,
    ...stats,
    isReturningUser: stats.visitCount > 1,
    daysSinceFirstVisit: stats.firstSeen
      ? Math.floor((Date.now() - new Date(stats.firstSeen).getTime()) / (1000 * 60 * 60 * 24))
      : 0,
  };
}
